import { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminGroupView, AdminUserView, AllocationOverview } from '../../api.js';
import { CREDITS_PATTERN, toCredits, toMillis } from '../../components/AllocationPanel.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import { CreditImportPanel } from './CreditImportPanel.js';
import { CreditSettlementPanel } from './CreditSettlementPanel.js';
import { SubjectAllocationPanel } from './SubjectAllocationPanel.js';

export type SubjectKind = 'group' | 'user';
export type MemberNodeKind = 'tenant' | 'group';
export type MemberCapMode = 'ceiling' | 'reservation';
type CreditImportStatus = 'ready' | 'applied' | 'failed';

export interface CreditImportRow {
  line: number;
  email: string;
  credit: string;
  userId?: string;
  errorKey?: string;
}

export interface CreditImportOutcome {
  status: CreditImportStatus;
  message: string;
  appliedCreditMillis?: number;
}

function parseCreditImportRows(text: string, users: AdminUserView[]): CreditImportRow[] {
  const usersByEmail = new Map(users.map((user) => [user.email.trim().toLowerCase(), user]));
  const seenUserIds = new Set<string>();

  return text
    .split(/\r?\n/)
    .map((line, index) => ({ raw: line.trim(), line: index + 1 }))
    .filter(({ raw }) => raw !== '')
    .flatMap(({ raw, line }, index) => {
      const [email = '', credit = ''] = raw.split(',').map((part, partIndex) =>
        (partIndex === 0 ? part.replace(/^\uFEFF/, '') : part).trim()
      );
      if (index === 0 && email.toLowerCase() === 'email' && credit.toLowerCase() === 'credit') return [];

      const user = usersByEmail.get(email.toLowerCase());
      let errorKey: string | undefined;
      if (!email || !email.includes('@')) errorKey = 'creditImport.invalidEmail';
      else if (!credit || !CREDITS_PATTERN.test(credit)) errorKey = 'creditImport.invalidCredit';
      else if (!user) errorKey = 'creditImport.userMissing';
      else if (seenUserIds.has(user.id)) errorKey = 'creditImport.duplicateUser';

      if (user && !errorKey) seenUserIds.add(user.id);
      return [{ line, email, credit, ...(user ? { userId: user.id } : {}), ...(errorKey ? { errorKey } : {}) }];
    });
}

/**
 * Index of the item the batch rejected, when the service named one.
 *
 * `validateCreditBatch` pre-validates every item and throws before applying
 * any, so exactly one item is at fault and nothing was written. Blaming every
 * row for it — the previous behaviour — hid which row to fix.
 */
function failedBatchIndex(error: unknown): number | undefined {
  const body = error && typeof error === 'object' && 'body' in error ? (error as { body: unknown }).body : undefined;
  const details =
    body && typeof body === 'object' && 'details' in body ? (body as { details: unknown }).details : undefined;
  const index =
    details && typeof details === 'object' && 'index' in details ? (details as { index: unknown }).index : undefined;
  return typeof index === 'number' && Number.isInteger(index) && index >= 0 ? index : undefined;
}

export function AllocationsScreen({ http, onToast }: { http: AdminHttpClient; onToast: (toast: ToastMessage) => void }) {
  const { t } = useTranslation();
  const [pool, setPool] = useState<AllocationOverview | undefined>();
  const [groups, setGroups] = useState<AdminGroupView[]>([]);
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [subjectKind, setSubjectKind] = useState<SubjectKind>('group');
  const [subjectId, setSubjectId] = useState('');
  const [subjectAllocation, setSubjectAllocation] = useState<AllocationOverview | undefined>();
  const [credit, setCredit] = useState('');
  const [importText, setImportText] = useState('');
  const [importOutcomes, setImportOutcomes] = useState<Record<number, CreditImportOutcome>>({});
  const [importBusy, setImportBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const fail = useCallback((error: unknown) => onToast({ tone: 'error', message: describeError(error, t) }), [onToast, t]);

  const subjects = useMemo(
    () =>
      subjectKind === 'group'
        ? groups.map((group) => ({ id: group.id, label: `${group.name} (${group.id})` }))
        : users.map((user) => ({ id: user.id, label: `${user.email} (${user.id})` })),
    [groups, subjectKind, users]
  );

  const importRows = useMemo(() => parseCreditImportRows(importText, users), [importText, users]);
  const importValidRows = useMemo(
    () => importRows.filter((row): row is CreditImportRow & { userId: string } => !row.errorKey && Boolean(row.userId)),
    [importRows]
  );
  const importInvalidCount = importRows.length - importValidRows.length;
  const importAppliedCount = importRows.filter((row) => importOutcomes[row.line]?.status === 'applied').length;
  const importFailedRows = importRows.filter((row) => importOutcomes[row.line]?.status === 'failed');
  const hasUniformImportCredit =
    importValidRows.length > 1 && new Set(importValidRows.map((row) => row.credit.trim())).size === 1;

  const loadBase = useCallback(async () => {
    try {
      const [tenantPool, loadedGroups, loadedUsers] = await Promise.all([
        http.tenantGetOwnAllocation(),
        http.listGroups(),
        http.listUsers()
      ]);
      setPool(tenantPool);
      setGroups(loadedGroups);
      setUsers(loadedUsers);
    } catch (error) {
      fail(error);
    }
  }, [fail, http]);

  const loadSubjectAllocation = useCallback(
    async (kind: SubjectKind, id: string) => {
      if (!id) {
        setSubjectAllocation(undefined);
        setCredit('');
        return;
      }
      try {
        const allocation = await http.getSubjectAllocation(kind, id);
        setSubjectAllocation(allocation);
        setCredit(allocation.credit.granted === undefined ? '' : String(toCredits(allocation.credit.granted)));
      } catch (error) {
        fail(error);
      }
    },
    [fail, http]
  );

  useEffect(() => {
    void loadBase();
  }, [loadBase]);

  useEffect(() => {
    if (subjects.length === 0) {
      setSubjectId('');
      return;
    }
    if (!subjects.some((subject) => subject.id === subjectId)) setSubjectId(subjects[0]?.id ?? '');
  }, [subjectId, subjects]);

  useEffect(() => {
    void loadSubjectAllocation(subjectKind, subjectId);
  }, [loadSubjectAllocation, subjectId, subjectKind]);


  function saveAllocation(event: FormEvent) {
    event.preventDefault();
    if (!subjectId) return;
    const raw = credit.trim();
    if (raw !== '' && !CREDITS_PATTERN.test(raw)) {
      onToast({ tone: 'error', message: t('allocations.allocInvalid') });
      return;
    }
    setBusy(true);
    void http
      .setSubjectAllocation(subjectKind, subjectId, {
        ...(raw !== '' ? { creditMillis: toMillis(raw) } : {})
      })
      .then(async () => {
        setSubjectAllocation(await http.getSubjectAllocation(subjectKind, subjectId));
        setPool(await http.tenantGetOwnAllocation());
        onToast({ tone: 'success', message: t('allocations.allocSaved') });
      })
      .catch(fail)
      .finally(() => setBusy(false));
  }


  function setImportSource(text: string) {
    setImportText(text);
    setImportOutcomes({});
  }

  function loadImportFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    void file.text().then(setImportSource).catch(fail);
  }

  function retryFailedImportRows() {
    setImportSource(['email,credit', ...importFailedRows.map((row) => `${row.email},${row.credit}`)].join('\n'));
  }

  function applyCreditImport(event: FormEvent) {
    event.preventDefault();
    if (importRows.length === 0) {
      onToast({ tone: 'error', message: t('creditImport.empty') });
      return;
    }
    if (importInvalidCount > 0 || importValidRows.length === 0) {
      onToast({ tone: 'error', message: t('creditImport.fixRows') });
      return;
    }

    setImportBusy(true);
    void http
      .setSubjectAllocationsBatch({
        items: importValidRows.map((row) => ({
          granteeKind: 'user',
          granteeId: row.userId,
          creditMillis: toMillis(row.credit)
        }))
      })
      .then(async (result) => {
        const nextOutcomes: Record<number, CreditImportOutcome> = {};
        for (const entry of result.results) {
          const row = importValidRows[entry.index];
          if (!row) continue;
          nextOutcomes[row.line] = {
            status: 'applied',
            message: t('creditImport.applied'),
            ...(entry.allocation.creditMillisGranted !== undefined ? { appliedCreditMillis: entry.allocation.creditMillisGranted } : {})
          };
        }
        for (const row of importValidRows) {
          nextOutcomes[row.line] ??= { status: 'failed', message: t('creditImport.noResult') };
        }
        setImportOutcomes(nextOutcomes);
        setPool(await http.tenantGetOwnAllocation());
        if (subjectKind === 'user' && importValidRows.some((row) => row.userId === subjectId)) {
          await loadSubjectAllocation(subjectKind, subjectId);
        }
        onToast({ tone: 'success', message: t('creditImport.saved', { count: result.results.length }) });
      })
      .catch((error) => {
        const message = describeError(error, t);
        const culpritIndex = failedBatchIndex(error);
        const culprit = culpritIndex === undefined ? undefined : importValidRows[culpritIndex];
        const blamed = culprit ? [culprit] : importValidRows;
        setImportOutcomes(
          Object.fromEntries(blamed.map((row) => [row.line, { status: 'failed', message } satisfies CreditImportOutcome]))
        );
        onToast({ tone: 'error', message });
      })
      .finally(() => setImportBusy(false));
  }

  const importDetails = useRef<HTMLDetailsElement>(null);
  const importError = useRef<HTMLParagraphElement>(null);
  const importErrorCount = importInvalidCount + importFailedRows.length;
  useEffect(() => {
    if (importErrorCount > 0 && importDetails.current) importDetails.current.open = true;
  }, [importErrorCount]);

  return (
    <section className="content-stack">
      <div className="panel">
        <h2>{t('allocations.title')}</h2>
        {pool && (
          <p className="muted">
            {pool.unlimited
              ? t('allocations.poolUnlimited', { consumed: toCredits(pool.credit.consumed) })
              : t('allocations.poolInfo', {
                  granted: pool.credit.granted === undefined ? 0 : toCredits(pool.credit.granted),
                  consumed: toCredits(pool.credit.consumed),
                  remaining: pool.credit.remaining === undefined ? 0 : toCredits(pool.credit.remaining)
                })}
          </p>
        )}
      </div>

      <SubjectAllocationPanel
        subjectKind={subjectKind}
        subjectId={subjectId}
        subjects={subjects}
        {...(subjectAllocation ? { subjectAllocation } : {})}
        credit={credit}
        busy={busy}
        onSubjectKindChange={(kind) => {
          setSubjectKind(kind);
          setSubjectId('');
        }}
        onSubjectIdChange={setSubjectId}
        onCreditChange={setCredit}
        onSave={saveAllocation}
      />

      {/* Member defaults are usage LIMITS, not budget, so PR-4 moved the panel
          to the Usage Limits screen. This screen stays about money. */}
      <p className="muted">{t('allocations.limitsMovedNote')}</p>

      <details className="allocation-secondary" ref={importDetails}>
        <summary>{t('creditImport.title')}<span>{t('allocations.importDescription')}</span></summary>
        {importErrorCount > 0 ? <p className="error" role="alert" tabIndex={-1} ref={importError}>{t('allocations.importErrors', { count: importErrorCount })}</p> : null}
      <CreditImportPanel
        importText={importText}
        importRows={importRows}
        importOutcomes={importOutcomes}
        importValidCount={importValidRows.length}
        importInvalidCount={importInvalidCount}
        importAppliedCount={importAppliedCount}
        importFailedCount={importFailedRows.length}
        hasUniformImportCredit={hasUniformImportCredit}
        importBusy={importBusy}
        onImportTextChange={setImportSource}
        onFileChange={loadImportFile}
        onRetryFailed={retryFailedImportRows}
        onApply={applyCreditImport}
      />
      </details>
      {importErrorCount > 0 ? <button type="button" className="secondary" onClick={() => {
        if (importDetails.current) importDetails.current.open = true;
        importError.current?.focus();
      }}>{t('allocations.goToErrors')}</button> : null}
      <details className="allocation-secondary">
        <summary>{t('creditSettlement.title')}<span>{t('allocations.settlementDescription')}</span></summary>
        <CreditSettlementPanel http={http} onToast={onToast} />
      </details>
    </section>
  );
}
